import type { TruentityDateInput } from '@/shared/lib/types/date';
import { CustomTimezone, DayOfWeek, GeneralTimezone } from '@/shared/lib/types/date';
import { timezoneNameMap, usTimezoneAbbreviationMap, utcOffsetMap } from '@/shared/lib/util/constants';
import type { DateInput } from '@fullcalendar/core';
import type { Moment, MomentInput } from 'moment';
import moment from 'moment';

export const toISOString = (date: Date | null) => {
  return date?.toISOString() || null;
};

// The calendar day the user picked, as YYYY-MM-DD in the browser's timezone. Unlike toISOString,
// local midnight is not shifted to another day by the UTC conversion. Empty or invalid input gives null.
export const toCalendarDate = (date: TruentityDateInput): string | null => {
  if (!date) return null;
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

export const getFirstDateOfPreviousMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth() - 1, 1);
};

export const getLastDateOfPreviousMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth(), 0);
};

export const secondsToDate = (seconds: number) => {
  return moment.utc(seconds * 1000);
};

export const addTwoMonthsFromDate = (date: Date) => {
  return moment(date).add(2, 'months');
};

export const getPreviousMonthStartMoment = (date: Moment): Moment => {
  const month = date.month();
  const year = date.year();
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  return moment().year(prevYear).month(prevMonth).date(1);
};

export const getPreviousMonthEndMoment = (date: Moment): Moment => {
  const month = date.month();
  const year = date.year();
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  return moment().year(prevYear).month(prevMonth).endOf('month');
};

export const getDateStringForMessage = (messageDate: DateInput) => {
  try {
    const adminTimezone = getGeneralTimezone(CustomTimezone.ADMIN_TIMEZONE);
    const adminToday = toTimezoneDate({ date: moment().startOf('day'), timezone: adminTimezone });
    const adminYesterday = toTimezoneDate({ date: moment().subtract(1, 'days').startOf('day'), timezone: adminTimezone });
    const adminMessageDay = toTimezoneDate({ date: moment(messageDate).startOf('day'), timezone: adminTimezone });

    if (adminMessageDay.isSame(adminToday, 'd')) {
      return 'Today';
    } else if (adminMessageDay.isSame(adminYesterday, 'd')) {
      return 'Yesterday';
    } else if (adminMessageDay.isSame(adminToday, 'w')) {
      return adminMessageDay.format('dddd');
    } else {
      return adminMessageDay.format('MMM D YYYY');
    }
  } catch (error) {
    return '';
  }
};

export const toDate = (date: MomentInput) => {
  return moment(date).toDate();
};

export const addOneYearToDate = (date: MomentInput) => {
  return moment(date).add(1, 'year');
};

export const isSameOrBefore = (date1: MomentInput, date2: MomentInput) => {
  return moment(date1).isSameOrBefore(date2);
};

export const getDaysBeforeDate = (date: MomentInput, days: number): Date => {
  return moment(date).subtract(days, 'days').toDate();
};

export const incrementMonths = (date: MomentInput, months: number) => {
  return moment(date).clone().add(months, 'months');
};

export const incrementYears = (date: MomentInput, years: number) => {
  return moment(date).clone().add(years, 'years');
};

export const incrementDates = (date: MomentInput, days: number): Date => {
  return moment(date).add(days, 'days').toDate();
};

export const subtractDates = (date: MomentInput, days: number): Date => {
  return moment(date).subtract(days, 'days').toDate();
};

// Overloaded because it mirrors its input: a Moment in, a Moment out; a Date in, a Date out.
// Without these every caller saw the `Date | Moment` union and had to cast.
/* eslint-disable no-redeclare -- the base rule does not understand TypeScript overloads. */
export function getFirstDateOfCurrentMonth(date: Moment): Moment;
export function getFirstDateOfCurrentMonth(date: Date): Date;
export function getFirstDateOfCurrentMonth(date: Date | Moment): Date | Moment;
export function getFirstDateOfCurrentMonth(date: Date | Moment): Date | Moment {
  if (moment.isMoment(date)) {
    return moment(date).startOf('month');
  } else {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }
}
/* eslint-enable no-redeclare */

export const getLstDateOfCurrentMonth = (date: Date | Moment) => {
  if (moment.isMoment(date)) {
    return moment(date).endOf('month');
  } else {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }
};

export const getYesterdayDateOfCurrentMonth = (date: Date): Date => {
  const yesterday = new Date(date);
  yesterday.setDate(date.getDate() - 1);
  return yesterday;
};

export const toDateOrNull = (date?: unknown) => {
  if (moment.isMoment(date) || moment.isDate(date)) {
    return date;
  }

  if (date && typeof date === 'string' && Date.parse(date)) {
    return new Date(date);
  }

  return null;
};

export const toDateOrDefault = (date: unknown, def: MomentInput) => {
  if (moment.isMoment(date) || moment.isDate(date)) {
    return date;
  }

  if (date && typeof date === 'string' && Date.parse(date)) {
    return new Date(date);
  }

  return def;
};

export const toMomentDate = (date: TruentityDateInput) => {
  return moment(date);
};

export const toMomentDateOrDefault = (date: unknown, def: Moment) => {
  if (moment.isMoment(date)) {
    return date;
  }

  if (date && typeof date === 'string' && date.length > 0 && Date.parse(date)) {
    return moment(date);
  }

  return def;
};

export const toUTC = (date: TruentityDateInput) => {
  return moment.utc(date);
};

export const today = (): Moment => {
  return moment.utc();
};

export const getDateDifferenceFromNow = (date: MomentInput): string => {
  return moment(date).fromNow();
};

export const tomorrow = () => {
  return moment.utc().add(1, 'day');
};

/*
 * Formats we round-trip dates through when they are held as display strings (e.g.
 * `formatTime(date, 'MMM YYYY')`).  A bare `moment(string)` falls back to the native Date
 * parser for these, which Safari and Firefox reject — producing an invalid Moment.  Parsed
 * strictly, so anything that is not an exact match still goes through moment's own detection.
 */
const TRUENTITY_DATE_INPUT_FORMATS = [
  moment.ISO_8601,
  moment.RFC_2822,
  'YYYY-MM-DD',
  'YYYY-MM-DD HH:mm:ss',
  'MM/DD/YYYY',
  'MMM YYYY',
  'MMMM YYYY',
  'MMM DD, YYYY'
];

export const toTruentityDateInput = (date: MomentInput) => {
  if (!date) {
    return null;
  }

  if (typeof date === 'string') {
    const parsed = moment(date, TRUENTITY_DATE_INPUT_FORMATS, true);
    if (parsed.isValid()) {
      return parsed;
    }

    const looselyParsed = moment(date);
    return looselyParsed.isValid() ? looselyParsed : null;
  }

  return moment(date);
};

export const dateToSeconds = (date: MomentInput) => {
  if (!date) {
    return 0;
  }
  const hour = moment(date).hour();
  const minute = moment(date).minute();
  const second = moment(date).second();
  return hour * 3600 + minute * 60 + second;
};

export const getMidnightUTC = () => {
  const now = new Date();
  const localMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  return localMidnight;
};

type ToTimezoneDateInput = {
  date: MomentInput;
  timezone: GeneralTimezone;
};

export const toTimezoneDate = ({ date, timezone = GeneralTimezone.EST }: ToTimezoneDateInput): Moment => {
  const localeString = toDate(date).toLocaleString('en-GB', { timeZone: timezone });
  return moment(localeString, 'DD/MM/YYYY HH:mm:ss');
};

export const getGeneralTimezone = (customTimezone: CustomTimezone): GeneralTimezone => {
  switch (customTimezone) {
    case CustomTimezone.ACCOUNT_TIMEZONE:
      return GeneralTimezone.EST;
    case CustomTimezone.ADMIN_TIMEZONE:
      return GeneralTimezone.EST;
    default:
      throw new Error('Invalid timezone');
  }
};

/*
 * Function to categorize a given date to say if it's Today, Yesterday, Last Week, Last Month or Older
 */
export const categorizeDate = (date: Date): string => {
  const today = new Date();

  // Normalize dates to remove time for comparison
  const normalizeDate = (inputDate: Date): Date => {
    return new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());
  };

  const inputDate = normalizeDate(date);
  const currentDate = normalizeDate(today);

  // Get the difference in milliseconds
  const diffInMs = currentDate.getTime() - inputDate.getTime();

  // Milliseconds to days
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays <= 7) {
    return 'Last week';
  } else if (diffInDays <= 30) {
    return 'Earlier this month';
  } else if (
    // Check the edge cases of the current month being January and the previous month being December
    (currentDate.getMonth() === 0 && inputDate.getMonth() === 11 && inputDate.getFullYear() === currentDate.getFullYear() - 1) ||
    (inputDate.getMonth() === currentDate.getMonth() - 1 && inputDate.getFullYear() === currentDate.getFullYear())
  ) {
    return 'Last month';
  } else {
    return 'Older';
  }
};

export const getDayOfWeek = (date: MomentInput): DayOfWeek => {
  const dayIndex = moment(date).day();
  const days: DayOfWeek[] = [
    DayOfWeek.SUNDAY,
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY
  ];
  return days[dayIndex];
};

export const isTimeInRange = (time: TruentityDateInput, startTime: string, endTime: string, dateContext?: TruentityDateInput): boolean => {
  if (!time || !startTime || !endTime) {
    return false;
  }

  try {
    const selectedDate = dateContext ? moment(dateContext) : moment(time);
    const selectedDateStr = selectedDate.format('YYYY-MM-DD');

    const startTimeComp = extractTimeComponents(startTime);
    const endTimeComp = extractTimeComponents(endTime);

    const startTimeStr = `${startTimeComp.hours.toString().padStart(2, '0')}:${startTimeComp.minutes.toString().padStart(2, '0')}`;
    const endTimeStr = `${endTimeComp.hours.toString().padStart(2, '0')}:${endTimeComp.minutes.toString().padStart(2, '0')}`;

    const startDateTimeEST = moment.tz(`${selectedDateStr} ${startTimeStr}`, 'YYYY-MM-DD HH:mm', GeneralTimezone.EST);
    const endDateTimeEST = moment.tz(`${selectedDateStr} ${endTimeStr}`, 'YYYY-MM-DD HH:mm', GeneralTimezone.EST);

    const startLocal = startDateTimeEST.clone().local();
    const endLocal = endDateTimeEST.clone().local();

    const timeMoment = moment(time);
    const timeValue = timeMoment.hours() * 60 + timeMoment.minutes();
    const startValue = startLocal.hours() * 60 + startLocal.minutes();
    const endValue = endLocal.hours() * 60 + endLocal.minutes();

    if (startValue <= endValue) {
      return timeValue >= startValue && timeValue <= endValue;
    } else {
      return timeValue >= startValue || timeValue <= endValue;
    }
  } catch (error) {
    console.error('Error in isTimeInRange:', error);
    return false;
  }
};

export const extractTimeComponents = (timeString: string): { hours: number; minutes: number } => {
  if (!timeString) {
    return { hours: 0, minutes: 0 };
  }

  const match = timeString.match(/(\d{2}):(\d{2}):/);
  return match
    ? {
        hours: parseInt(match[1], 10),
        minutes: parseInt(match[2], 10)
      }
    : { hours: 0, minutes: 0 };
};

const getBrowserFormattedTimezone = (): string => {
  const browserTz =
    new Intl.DateTimeFormat('en', { timeZoneName: 'short' }).formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value ||
    '';

  return browserTz.replace('GMT', '').replace(/^([+-]\d+):(\d+)$/, '');
};

export const getTimezoneAbbreviation = (timezone?: string): string => {
  const tzGuess = timezone || moment.tz.guess();

  if (timezoneNameMap[tzGuess]) {
    return timezoneNameMap[tzGuess];
  }
  const abbr = moment.tz(tzGuess).zoneAbbr();
  const isAmericanZone = tzGuess.startsWith('America/') || tzGuess === 'Pacific/Honolulu';
  if (isAmericanZone && usTimezoneAbbreviationMap[abbr]) {
    return usTimezoneAbbreviationMap[abbr];
  }
  if (!/^[+-]/.test(abbr)) {
    return abbr;
  }

  const offset = moment.tz(tzGuess).utcOffset();
  if (utcOffsetMap[offset]) {
    return utcOffsetMap[offset];
  }

  return getBrowserFormattedTimezone();
};

export const calculateTotalSeconds = (minutes: number | null | undefined): number => {
  return Math.round((minutes ?? 0) * 60);
};

export const getDiffBetweenTimesInMinutes = (firstDateTime: Date, secondDateTime: Date) => {
  const differenceInMinutes = (firstDateTime.getTime() - secondDateTime.getTime()) / (1000 * 60);
  return differenceInMinutes;
};

export const isValidDate = (date: Date | undefined | null): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};

export const toStartOfDayUTC = (date: MomentInput): Moment | null => {
  if (!date) {
    return null;
  }
  return moment(date).startOf('day').utc();
};
